import L from 'leaflet'
import 'leaflet-draw'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'

const DEFAULTS = {
  mapOptions: {
    zoomControl: false,
    doubleClickZoom: false
  },
  center: [-3.580, -57.775],
  zoom: 4.5,
  position: 'topright',
  minZoom: 4,
  maxZoom: 17,
  baseMap: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    name: 'Esri Imagery'
  },
  drawingTools: {
    polygon: {
      showArea: true,
      showLength: true,
      precision: {
        km: 1,
        ha: 1,
        m: 0
      },
      shapeOptions: {
        color: '#3487FC'
      }
    },
    polyline: false,
    circle: false,
    marker: false,
    rectangle: false,
    circlemarker: false
  },
  texts: {
    draw: {
      toolbar: {
        actions: {
          title: 'Cancelar desenho',
          text: 'Cancelar'
        },
        finish: {
          title: 'Terminar desenho',
          text: 'Terminar'
        },
        undo: {
          title: 'Apagar último ponto desenhado',
          text: 'Apagar último ponto'
        },
        buttons: {
          polyline: 'Desenhar uma linha',
          polygon: 'Desenhar um polígono',
          rectangle: 'Desenhar um retângulo',
          circle: 'Desenhar um círculo',
          marker: 'Desenhar um marcador',
          circlemarker: 'Desenhar um marcador de círculo'
        }
      },
      handlers: {
        circle: {
          tooltip: {
            start: 'Clique e arraste para desenhar círculo'
          },
          radius: 'Raio'
        },
        circlemarker: {
          tooltip: {
            start: 'Clique no mapa para colocar um marcador de círculo'
          }
        },
        marker: {
          tooltip: {
            start: 'Clique no mapa para colocar um marcador'
          }
        },
        polygon: {
          tooltip: {
            start: 'Clique para começar a desenhar forma',
            cont: 'Clique para continuar desenhando forma',
            end: 'Clique no primeiro ponto para fechar esta forma'
          }
        },
        polyline: {
          error: '<strong>Erro:</strong> as bordas da forma não podem se cruzar!',
          tooltip: {
            start: 'Clique para começar a desenhar linha',
            cont: 'Clique para continuar desenhando linha',
            end: 'Clique no último ponto para terminar a linha'
          }
        },
        rectangle: {
          tooltip: {
            start: 'Clique e arraste para desenhar retângulo'
          }
        },
        simpleshape: {
          tooltip: {
            end: 'Solte o mouse para terminar o desenho'
          }
        }
      }
    },
    edit: {
      toolbar: {
        actions: {
          save: {
            title: 'Salvar alterações',
            text: 'Salvar'
          },
          cancel: {
            title: 'Cancelar edição, descartar todas as alterações',
            text: 'Cancelar'
          },
          clearAll: {
            title: 'Limpar todas as camadas',
            text: 'Limpar tudo'
          }
        },
        buttons: {
          edit: 'Editar camadas',
          editDisabled: 'Nenhuma camada para editar',
          remove: 'Apagar camadas',
          removeDisabled: 'Nenhuma camada para apagar'
        }
      },
      handlers: {
        edit: {
          tooltip: {
            text: 'Arraste os pontos ou marcadores para editar a forma',
            subtext: 'Clique em cancelar para desfazer as alterações'
          }
        },
        remove: {
          tooltip: {
            text: 'Clique em uma forma para removê-la'
          }
        }
      }
    }
  }
}

export default class MapManager {
  _map = null
  _layers = null
  _zoomControl = null
  _drawingControls = null
  _drawItemsGroup = null
  _enableEdit = false
  _isLoading = false

  constructor (mapId, enableEdit = false) {
    this._map = L.map(mapId, DEFAULTS.mapOptions)
    this._enableEdit = enableEdit
    this._init()
  }

  _init () {
    this._setBaseMapView()
    this._addZoomControl()
    this._addBaseLayers()
    this._setDrawingControls()
    this.toggleDrawing(false)
  }

  _setBaseMapView () {
    this._map.setView(DEFAULTS.center, DEFAULTS.zoom)
  }

  _addZoomControl () {
    this._zoomControl = L.control.zoom({ position: DEFAULTS.position }).addTo(this._map)
  }

  _addBaseLayers () {
    L.tileLayer(DEFAULTS.baseMap.url, {
      attribution: `© ${DEFAULTS.baseMap.name}`,
      minZoom: DEFAULTS.minZoom,
      maxZoom: DEFAULTS.maxZoom
    }).addTo(this._map)
  }

  _setDrawingControls () {
    this._drawItemsGroup = new L.FeatureGroup()
    this._map.addLayer(this._drawItemsGroup)

    this._drawingControls = new L.Control.Draw({
      position: 'topright',
      edit: {
        featureGroup: this._drawItemsGroup,
        edit: this._enableEdit
      },
      draw: {
        ...DEFAULTS.drawingTools
      }
    })

    this._map.addControl(this._drawingControls)

    L.drawLocal = DEFAULTS.texts
  }

  _enableLoading () {
    this._lockMapActions()
    this._isLoading = true

    const loadingDiv = document.createElement('div')
    loadingDiv.className = 'sedap-map-loading'
    loadingDiv.innerHTML = `
      <div class="sedap-map-loader"></div>
    `
    loadingDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      background: rgba(197, 197, 197, 0.8);
      transform: translate(-50%, -50%);
      color: white;
      width: 100%;
      height: 100%;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(197, 197, 197, 0.8);
      z-index: 10000;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: all;
    `

    const style = document.createElement('style')
    style.textContent = `
      .sedap-map-loader {
        border: 6px solid #009f61;
        border-top: 6px solid transparent;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `

    document.head.appendChild(style)

    this._map._container.appendChild(loadingDiv)
  }

  _lockMapActions () {
    this._map.dragging.disable()
    this._map.scrollWheelZoom.disable()
    this._map.doubleClickZoom.disable()
    this._map.boxZoom.disable()
    this._map.keyboard.disable()
  }

  _unlockMapActions () {
    this._map.dragging.enable()
    this._map.scrollWheelZoom.enable()
    this._map.doubleClickZoom.enable()
    this._map.boxZoom.enable()
    this._map.keyboard.enable()
  }

  _disableLoading () {
    this._isLoading = false

    const loadingDiv = document.querySelector('.sedap-map-loading')
    if (loadingDiv) {
      loadingDiv.remove()
    }

    const loaderStyle = Array.from(document.head.querySelectorAll('style'))
      .find(tag => tag.textContent.includes('.sedap-map-loader'))

    if (loaderStyle) {
      loaderStyle.remove()
    }

    this._unlockMapActions()
  }

  _showAlert (message, duration = 2000) {
    const alertDiv = document.createElement('div')

    alertDiv.className = 'sedap-map-alert'
    alertDiv.innerHTML = `<div class="sedap-map-alert">
      <i class="fas fa-exclamation-triangle"></i>
      ${message}
      </div>
    `
    alertDiv.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #ff6b6b;
      color: white;
      padding: 10px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 10px;
      animation: slideIn 0.3s ease;
      pointer-events: none;
      display: inline-flex;
      white-space: nowrap;
    `

    const style = document.createElement('style')
    style.textContent = `
      .sedap-map-alert {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #ff6b6b;
        color: white;
        padding: 10px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-weight: bold;
        display: flex;
        align-items: center;
        gap: 10px;
        animation: slideIn 0.3s ease;
      }
      @keyframes slideIn {
        from { opacity: 0; transform: translate(-50%, -60%); }
        to { opacity: 1; transform: translate(-50%, -50%); }
      }
    `
    document.head.appendChild(style)

    this._map._container.appendChild(alertDiv)

    setTimeout(() => {
      if (alertDiv.parentNode) {
        alertDiv.remove()
      }
      if (style.parentNode) {
        style.remove()
      }
    }, duration)
  }

  toggleDrawing (value) {
    if (value) {
      this._map.addControl(this._drawingControls)
    } else {
      this._map.removeControl(this._drawingControls)
    }
  }

  get map () {
    return this._map
  }

  get drawItemsGroup () {
    return this._drawItemsGroup
  }

  get drawingControls () {
    return this._drawingControls
  }

  setDefaultMapPosition () {
    this._map.setView(DEFAULTS.center, DEFAULTS.zoom)
  }
}



// WEBPACK FOOTER //
// ./src/components(new)/UI/Maps/core/mapManager.js